import parse, { type DOMNode, domToReact, type Element, type HTMLReactParserOptions } from 'html-react-parser'
import Script from 'next/script'
import { Fragment } from 'react'

import { CardAutoLink } from '@/components/common/Card'
import { CardGrid } from '@/components/common/CardGrid'
import { CSBEmbed } from '@/components/common/CSBEmbed'
import { DeckEmbed } from '@/components/common/DeckEmbed'
import { EmbedArticle } from '@/components/common/EmbedArticle'
import { Footnote } from '@/components/common/Footnote'
import { LinkHash, LinkOutbound } from '@/components/common/Link'
import { type ArticleReportType } from '@/defs/articleReport'
import { slugify } from '@/utils/commonUtil'
import { buildRec } from '@/utils/recUtil'

import { type ParsedContentContextType } from './ParsedContentProvider'

const CLASS_EMBEDDED = 'embedded'

export type ParseContentProps = {
  articleSlug?: string
  articleTitle?: string
  content: string
  getReport?: boolean
}

type GetRecsProps = {
  category: 'cardEmbed' | 'cardLink'
  classList: string[]
  nameRaw?: string
}

const findChildren = (node: Element, name: string, className: string) =>
  node.children.filter(
    (child): child is Element =>
      child.type === 'tag' && child.name === name && !!child.attribs.class?.split(' ').includes(className),
  )

export const parseContent = ({
  articleSlug,
  articleTitle,
  content,
  getReport,
}: ParseContentProps): ParsedContentContextType => {
  const keyMap = new WeakMap<any, number>()
  let keyCounter = 0
  const reports: ArticleReportType[] = []

  const getKey = (node: DOMNode) => {
    if (!keyMap.has(node)) keyMap.set(node, keyCounter++)
    return keyMap.get(node) ?? keyCounter
  }

  const getRecs = ({ category, classList, nameRaw }: GetRecsProps) => {
    const [name] = nameRaw?.split('|') || []

    if (!nameRaw || !name) {
      report({ category, message: 'No card name provided.', status: 'embedError' })
      return []
    }
    const slug = slugify(nameRaw)

    if (classList.includes('side-by-side')) {
      const rec = buildRec({ name, onlyFront: true, slug })
      const recBack = buildRec({ name, onlyBack: true, slug })
      report({ category, rec, status: 'cardLoading', subject: rec.name })
      report({ category, rec: recBack, status: 'cardLoading', subject: recBack.name })
      return [rec, recBack]
    }

    const rec = buildRec({
      name,
      onlyBack: classList.includes('only-back'),
      onlyFront: classList.includes('only-front'),
      reverse: classList.includes('reverse'),
      slug,
    })

    report({ category, rec, status: 'cardLoading', subject: rec.name })

    return [rec]
  }

  const report = (entry: ArticleReportType) => {
    if (getReport) reports.push(entry)
  }

  const toReact = (nodes: Element['children']) => domToReact(nodes as DOMNode[], options)

  const options: HTMLReactParserOptions = {
    replace(node) {
      if (!('children' in node) || !('attribs' in node)) return
      const classList = node.attribs.class?.split(' ') || []
      const key = getKey(node)

      // Commander Spellbook loading placeholder, replaced by CSBEmbed
      if (node.attribs.id === 'csbLoad') return <Fragment key={`csb-${key}`} />

      // [ec]
      if (classList.includes('edhrecp__cards')) {
        const recs = findChildren(node, 'div', 'edhrecp__card-container').flatMap((child) =>
          getRecs({ category: 'cardEmbed', classList, nameRaw: child.attribs.name }),
        )

        if (!recs.length) return

        return (
          <div className={CLASS_EMBEDDED} key={`ec-${key}`}>
            <CardGrid recs={recs} variant='embedded' />
          </div>
        )
      }

      // [el]
      else if (classList.includes('edhrecp__link')) {
        const children = findChildren(node, 'a', 'edhrecp__link-a').map((child, j) => {
          const [rec] = getRecs({ category: 'cardLink', classList, nameRaw: child.attribs.content })

          if (!rec) return

          const content = child.children[0] && 'data' in child.children[0] ? child.children[0].data : rec.name

          return (
            <CardAutoLink className={node.attribs.class} key={`el-${key}-${j}`} rec={rec}>
              {content}
            </CardAutoLink>
          )
        })
        return (
          <span className={node.attribs.class} key={`el-${key}`}>
            {children}
          </span>
        )
      }

      // [ed]
      else if (classList.includes('edhrecp__deck')) {
        const children = findChildren(node, 'span', 'edhrecp__deck-s').map((child, j) => {
          const deckKey = `ed-${key}-${j}`
          const deckTitle = child.attribs.name || ''

          const cards = child.attribs.cards
          if (!cards) {
            report({ category: 'deckEmbed', message: 'No decklist found.', status: 'embedError', subject: deckTitle })
            return
          }

          const titleLength = (articleTitle?.length || 0) + deckTitle.length
          if (titleLength >= 125) {
            report({
              category: 'deckEmbed',
              message: `Generated Archidekt deck title too long (${titleLength} characters). Please shorten the deck title or the post title.`,
              status: 'embedError',
              subject: deckTitle,
            })
            return
          }

          const archidektLink =
            child.attribs.archidekt_link ||
            (articleSlug && deckTitle
              ? `https://archidekt.com/articles/edhrec/${articleSlug}/${slugify(deckTitle.replaceAll('-', ''))}`
              : undefined)

          report({ category: 'deckEmbed', status: 'embedSuccess', subject: deckTitle })

          return <DeckEmbed archidektLink={archidektLink} key={deckKey} rawDeck={cards} title={deckTitle} />
        })
        return (
          <div className={CLASS_EMBEDDED} key={`ed-${key}`}>
            {children}
          </div>
        )
      }

      // [mana]
      else if (classList.includes('mana-container')) {
        report({ category: 'mana', status: 'embedSuccess' })

        return <span key={`mana-${key}`}>{toReact(node.children)}</span>
      }

      // [efn_note]
      else if (classList.includes('easy-footnote')) {
        const child = node.children[0]
        const noteChild = child && 'children' in child ? child.children[0] : undefined
        const noteContent = noteChild && 'children' in noteChild ? noteChild.children : undefined
        const noteTitle = child && 'attribs' in child ? child.attribs.title : undefined

        if (!noteContent) {
          report({ category: 'footnote', message: 'Footnote markup could not be read.', status: 'embedError' })
          return
        }

        report({ category: 'footnote', status: 'embedSuccess', subject: noteTitle })

        return <Footnote content={toReact(noteContent)} key={`efn-${key}`} title={noteTitle} />
      }

      // WP embeds
      else if (node.type === 'tag' && node.attribs.class?.includes('wp-embedded-content')) {
        if (node.name === 'blockquote') {
          const child = node.children[0]
          if (!child || !('children' in child)) return
          const childChild = child.children[0]
          if (!childChild || !('attribs' in childChild)) return
          const match = childChild.attribs.href
          if (!match || !match.startsWith('https://edhrec.com')) {
            report({
              category: 'wpEmbed',
              message: 'Embedded link is not an EDHREC article, so it renders as a plain link.',
              status: 'embedError',
            })
            return toReact([childChild])
          }
          report({ category: 'wpEmbed', status: 'embedSuccess', subject: match })
          return (
            <div className={CLASS_EMBEDDED}>
              <EmbedArticle slug={match.split('/')[4] || ''} />
            </div>
          )
        } else if (node.name === 'iframe') {
          return <Fragment key={`wp-${key}`} />
        }
        return
      }

      // WP embeds (if previous format disabled by plugin)
      else if (node.type === 'tag' && node.name === 'p') {
        const child = node.children[0]
        if (!child || !('data' in child)) return
        const match = child.data.trim()
        if (!match || !match.startsWith('https://edhrec.com')) return
        report({ category: 'wpEmbed', status: 'embedSuccess', subject: match })
        return (
          <div className={CLASS_EMBEDDED}>
            <EmbedArticle slug={match.split('/')[4] || ''} />
          </div>
        )
      }

      // Misc links
      else if (node.type === 'tag' && node.name === 'a') {
        // Anchor target
        if (node.attribs.id) {
          return (
            <a id={node.attribs.id} key={`a-${key}`}>
              {toReact(node.children)}
            </a>
          )
        }

        // Anchor link
        if (node.attribs.href?.startsWith('#')) {
          return (
            <LinkHash hash={node.attribs.href.slice(1)} key={`a-${key}`}>
              {toReact(node.children)}
            </LinkHash>
          )
        }

        // Relative link or mailto
        if (!node.attribs.href?.startsWith('http')) {
          return (
            <a href={node.attribs.href} key={`a-${key}`}>
              {toReact(node.children)}
            </a>
          )
        }

        return (
          <LinkOutbound href={node.attribs.href} key={`a-${key}`} utm={{ medium: 'article' }}>
            {toReact(node.children)}
          </LinkOutbound>
        )
      }

      // <noscript> (iframes)
      else if (node.name === 'noscript') {
        return <Fragment key={`ns-${key}`} />
      }

      // <script>
      else if (node.type === 'script' && node.name === 'script') {
        if (node.attribs.src) {
          if (node.attribs.src.startsWith('https://commanderspellbook.com/embed.js?v=1')) {
            const parent = node.parent
            const id = parent && 'attribs' in parent ? parent.attribs.id : undefined
            if (!id) {
              report({ category: 'csbEmbed', message: 'No ID Provided.', status: 'embedError' })
              return
            }
            report({ category: 'csbEmbed', status: 'embedSuccess', subject: id })
            return (
              <div className={CLASS_EMBEDDED}>
                <CSBEmbed id={id} key={`s-${key}`} url={node.attribs.src} />
              </div>
            )
          } else if (node.attribs.src.includes('.polldaddy.com/p/')) {
            const container = node.attribs.src.match(/.polldaddy.com\/p\/(\d+)\.js/)?.[1]

            if (!container) {
              report({ category: 'polldaddy', message: 'Could not read poll ID.', status: 'embedError' })
              return
            }

            report({ category: 'polldaddy', status: 'embedSuccess', subject: container })

            return (
              <Fragment key={`s-${key}`}>
                <Script src={node.attribs.src} />
                <div id={`PDI_container${container}`} />
              </Fragment>
            )
          } else {
            const src = node.attribs.src
            report({
              category: 'scriptSrc',
              status: 'embedSuccess',
              subject: src,
            })
            return <Script key={`s-${key}`} src={src} />
          }
        } else if (node.children.length) {
          const children = node.children[0]
          const script = children && 'data' in children ? children.data : undefined

          if (!script) {
            report({
              category: 'scriptInline',
              message: 'Script inline embed error, script not found.',
              status: 'embedError',
            })
            return
          }

          report({ category: 'scriptInline', status: 'embedSuccess' })

          return (
            <Script id={`s-${key}`} key={`s-${key}`}>
              {script}
            </Script>
          )
        } else {
          report({ category: 'scriptEmpty', status: 'embedSuccess' })
          return (
            <script key={`s-${key}`} {...node.attribs}>
              {node.children.map((child, j) => (
                <Fragment key={`s-${key}-${j}`}>{toReact([child])}</Fragment>
              ))}
            </script>
          )
        }
      }
    },
  }

  return { content: parse(content, options), reports: getReport ? reports : undefined }
}
